import React from 'react';
import {loadOneTrust} from '../utils/loadOneTrust';
import {nodeId} from '../../../libs/react-widgets';

/**
 * OneTrust cookie banner provider
 * Requires cookieBanner.provider === 'onetrust' and cookieBanner.config.domainId
 */
export const onetrustProvider = {
  /**
   * Default trigger element ID for OneTrust
   */
  defaultTriggerId: 'onetrustTrigger',

  /**
   * Checks if OneTrust provider should be used based on settings
   * @param {Object} settings - Application settings
   * @returns {boolean}
   */
  shouldUse(settings) {
    return !!(settings.cookieBanner?.provider === 'onetrust' && settings.cookieBanner?.config?.domainId);
  },

  /**
   * Checks if initialization is needed (settings changed)
   * @param {Object} settings - Current settings
   * @param {Object} prevSettings - Previous settings
   * @returns {boolean}
   */
  shouldInitialize(settings, prevSettings) {
    const currentDomainId = settings.cookieBanner?.config?.domainId;
    const prevDomainId = prevSettings?.cookieBanner?.config?.domainId;
    return !!(currentDomainId && currentDomainId !== prevDomainId);
  },

  /**
   * Initializes OneTrust script
   * @param {Object} settings - Application settings
   * @param {Function} onError - Error callback (message, error)
   * @returns {Promise}
   */
  initialize(settings, onError) {
    const domainId = settings.cookieBanner?.config?.domainId;

    if (!domainId) {
      return Promise.reject(new Error('OneTrust domainId not found in settings'));
    }

    return loadOneTrust(domainId).catch((e) => {
      onError('Error loading OneTrust.', e);
      throw e;
    });
  },

  /**
   * Attaches click handler to trigger element (idempotent)
   * @param {string} triggerId - Element ID to attach handler to
   * @param {Function} onError - Error callback (message, error)
   */
  attachHandler(triggerId, onError) {
    const link = document.getElementById(triggerId || this.defaultTriggerId);
    if (!link) {
      return;
    }

    // Prevent duplicate handlers
    if (link.dataset.handlerAttached === 'true') {
      return;
    }

    link.href = '#';
    link.onclick = (e) => {
      e.preventDefault();
      if (window.OneTrust && typeof window.OneTrust.ToggleInfoDisplay === 'function') {
        window.OneTrust.ToggleInfoDisplay();
      } else {
        onError('OneTrust not ready.', new Error('OneTrust.ToggleInfoDisplay not available'));
      }
    };
    link.dataset.handlerAttached = 'true';
  },

  /**
   * Returns React style elements for OneTrust
   * @param {Object} settings - Application settings
   * @returns {Array<React.Element>}
   */
  getStyles(settings) {
    const styles = [];

    const oneTrustStyles = settings.styles?.cookieBanner?.onetrust;

    if (!oneTrustStyles) {
      return styles;
    }

    if (oneTrustStyles.fontSize || oneTrustStyles.tooltip?.fontSize) {
      styles.push(
        <style key={nodeId()}>{`
          .ot-sdk-container,
          .ot-floating-button,
          .ot-pc-scrollbar {
            font-size: ${oneTrustStyles.fontSize || '14px'};
          }
          ${oneTrustStyles.tooltip?.fontSize ? `
          .ot-floating-button::after {
            font-size: ${oneTrustStyles.tooltip.fontSize} !important;
          }` : ''}
        `}</style>
      );
    }

    return styles;
  }
};
